<template>
  <section class="app-support">
    <div class="app-support__leaf" aria-hidden="true"></div>
    <div class="container">
      <h2 class="app-support__title">{{ $t('zdraveto-app-support-title') }}</h2>

      <div class="app-support__grid">
        <div class="app-support__phone">
          <img src="/static/momfident_phone_screen.png" alt="Momfident App" />
        </div>

        <div class="app-support__features">
          <div v-for="(f, i) in features" :key="i" class="feature-row">
            <div class="feature-row__num">0{{ i + 1 }}</div>
            <div class="feature-row__body">
              <h4 class="feature-row__title">{{ $t(f.titleKey) }}</h4>
              <p class="feature-row__text">{{ $t(f.textKey) }}</p>
            </div>
          </div>

          <div class="app-support__actions">
            <button v-if="!hideCta" class="btn-white" type="button" @click="goToDownload">
              {{ $t('zdraveto-cta-btn') }}
            </button>
            <div class="store-badges">
              <a
                href="https://apps.apple.com/us/app/momfident/id6751100834"
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppStore />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.momfident.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                <GooglePlay />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import defineProps from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AppStore from '@/components/Common/Buttons/AppStore.vue';
import GooglePlay from '@/components/Common/Buttons/GooglePlay.vue';

defineProps({
  hideCta: {
    type: Boolean,
    default: false,
  },
});

const { locale } = useI18n();
const router = useRouter();

const features = [
  { titleKey: 'zdraveto-feature-1-title', textKey: 'zdraveto-feature-1-text' },
  { titleKey: 'zdraveto-feature-2-title', textKey: 'zdraveto-feature-2-text' },
  { titleKey: 'zdraveto-feature-3-title', textKey: 'zdraveto-feature-3-text' },
];

function goToDownload() {
  const targetRoute = locale.value === 'en' ? 'download-en' : 'download';
  router.push({ name: targetRoute });
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/fonts.scss";
@import "~@/assets/scss/variables.scss";

.app-support {
  margin-top: 100px;
  background: $color-pink-dark-2;
  padding: 88px 0 88px;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.app-support__leaf {
  position: absolute;
  bottom: -60px;
  right: -80px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.18), transparent 70%);
  filter: blur(6px);
  pointer-events: none;
}

.app-support__title {
  font-family: 'Rubik', sans-serif;
  font-weight: 300;
  font-size: 40px;
  text-align: center;
  margin: 0 0 44px;
  line-height: 1.1;
  letter-spacing: -0.5px;
}

.app-support__grid {
  display: grid;
  grid-template-columns: 5fr 7fr;
  gap: 56px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.app-support__phone {
  display: flex;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 500px;
    filter: drop-shadow(0 24px 48px rgba(0, 0, 0, 0.25));
  }
}

.feature-row {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  &:last-of-type {
    border-bottom: none;
  }

  &__num {
    font-family: 'Rubik', sans-serif;
    font-weight: 300;
    font-size: 40px;
    opacity: 0.5;
    line-height: 1;
    min-width: 50px;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: $font-semi-bold;
    font-weight: 500;
    font-size: 17px;
    margin: 6px 0 8px;
    line-height: 1.3;
  }

  &__text {
    font-size: 13px;
    margin: 0;
    line-height: 1.6;
    opacity: 0.9;
  }
}

.app-support__actions {
  margin-top: 28px;
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
}

.store-badges {
  display: flex;
  gap: 12px;
  align-items: center;

  a {
    display: inline-flex;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-3px);
    }
  }

  :deep(svg) {
    height: 44px;
    width: auto;
  }
}

.btn-white {
  background: #fff;
  color: $color-pink-dark-2;
  border: none;
  padding: 13px 32px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
}

.btn-white:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 28px rgba(0, 0, 0, 0.22);
}

.btn-white:active {
  transform: translateY(0);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
}

@media (max-width: 1100px) {
  .app-support__title {
    font-size: 32px;
  }
}

@media (max-width: 992px) {
  .app-support {
    padding: 84px 0 84px;
    margin-top: 72px;
  }

  .app-support__title {
    font-size: 28px;
    margin-bottom: 36px;
  }

  .app-support__grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .app-support__phone img {
    max-height: 360px;
  }
}

@media (max-width: 768px) {
  .app-support {
    padding: 72px 0 72px;
    margin-top: 56px;
  }

  .app-support__title {
    font-size: 24px;
    margin-bottom: 28px;
  }

  .feature-row {
    gap: 14px;
    padding: 16px 0;

    &__num {
      font-size: 32px;
      min-width: 42px;
    }

    &__title {
      font-size: 15px;
    }

    &__text {
      font-size: 12px;
    }
  }

  .btn-white {
    width: 100%;
  }

  .store-badges {
    justify-content: center;

    :deep(svg) {
      height: 38px;
    }
  }
}
</style>

